import debugFn from 'debug';
import { MinimumSegment } from '../../../domain/segments';
import { Timestamp } from '../../../types/date';
import { IPlayerDataProvider } from '../types';
import { PlaybackTech, SegmentWithAudio, SegmentWithSource } from './types';

const debug = debugFn('audio:segment-provider');

export type SegmentData = SegmentWithAudio | SegmentWithSource;

/**
 * SegmentProvider handles fetching and caching of audio segments.
 * Supports seeking to any timestamp by fetching the appropriate segment.
 */
export class SegmentProvider {
  private segments: MinimumSegment[] = [];
  private currentIndex = -1;
  private prefetchingNext = false;
  private nextSegmentData: SegmentData | null = null;

  constructor(
    private readonly dataProvider: IPlayerDataProvider,
    private readonly tech: PlaybackTech = PlaybackTech.MEDIA_SOURCE,
  ) {}

  /**
   * Get the segment containing the given timestamp.
   * Fetches from API if not in cache.
   */
  async getSegmentAt(ts: Timestamp): Promise<SegmentData | null> {
    debug('getSegmentAt ts=%d', ts);

    // Check if we have it in current sequence
    let segment = this.findSegmentForTs(ts);

    if (!segment) {
      // Fetch new sequence starting from this timestamp
      debug('segment not in cache, fetching sequence');
      await this.fetchSequence(ts);
      segment = this.findSegmentForTs(ts);
    }

    if (!segment) {
      debug('no segment found for ts=%d', ts);
      return null;
    }

    // Update current index
    this.currentIndex = this.segments.indexOf(segment);

    // Fetch audio data for this segment
    return this.fetchSegmentData(segment);
  }

  /**
   * Get next segment after the given timestamp.
   * Used for prefetching.
   */
  async getNextSegment(afterTs: Timestamp): Promise<SegmentData | null> {
    if (this.prefetchingNext) {
      return null;
    }

    this.prefetchingNext = true;

    try {
      // Use index-based lookup to match advanceSegment behavior
      const nextIndex = this.currentIndex + 1;
      let nextSegment: MinimumSegment | undefined =
        nextIndex < this.segments.length ? this.segments[nextIndex] : undefined;

      if (!nextSegment) {
        // Try to fetch more segments
        debug('fetching more segments after ts=%d', afterTs);
        await this.fetchSequence(afterTs);
        // After fetching, check if we now have the next segment
        nextSegment = nextIndex < this.segments.length ? this.segments[nextIndex] : undefined;

        if (!nextSegment) {
          debug('no more segments available');
          return null;
        }
      }

      this.nextSegmentData = await this.fetchSegmentData(nextSegment);
      return this.nextSegmentData;
    } finally {
      this.prefetchingNext = false;
    }
  }

  /**
   * Advance to the next segment (called when current segment ends).
   */
  advanceSegment(): MinimumSegment | null {
    if (this.currentIndex < 0 || this.currentIndex >= this.segments.length - 1) {
      return null;
    }

    this.currentIndex++;
    this.nextSegmentData = null;
    return this.segments[this.currentIndex];
  }

  /**
   * Check if currently prefetching next segment.
   */
  isPrefetching(): boolean {
    return this.prefetchingNext;
  }

  private findSegmentForTs(ts: Timestamp): MinimumSegment | undefined {
    return this.segments.find((s) => s.startTs <= ts && s.endTs > ts);
  }

  private async fetchSequence(ts: Timestamp): Promise<void> {
    debug('fetching sequence from ts=%d', ts);
    const sequence = await this.dataProvider.fetchSequence(ts);

    if (sequence.length === 0) {
      debug('empty sequence returned');
      return;
    }

    // Merge into existing segments, avoiding duplicates
    for (const segment of sequence) {
      const exists = this.segments.some((s) => s.id === segment.id);
      if (!exists) {
        this.segments.push(segment);
      }
    }

    // Sort by startTs
    this.segments.sort((a, b) => a.startTs - b.startTs);

    debug('sequence merged, total segments: %d', this.segments.length);
  }

  private async fetchSegmentData(segment: MinimumSegment): Promise<SegmentData> {
    debug('fetching audio for segment id=%d', segment.id);

    // For AUDIO_SRC tech, fetch as blob and create object URL for full seeking capability
    if (this.tech === PlaybackTech.AUDIO_SRC) {
      try {
        const audioUrl = this.dataProvider.generateAudioURL(segment.id);
        const response = await fetch(audioUrl);
        const blob = await response.blob();
        const audioSrc = URL.createObjectURL(blob);
        debug('created blob URL for segment id=%d', segment.id);
        return { segment, audioSrc };
      } catch (error) {
        debug('error fetching audio blob: %s', error);
        // Fallback to direct URL (seeking may be limited)
        const audioSrc = this.dataProvider.generateAudioURL(segment.id);
        return { segment, audioSrc };
      }
    }

    // For MEDIA_SOURCE tech, fetch audio buffer (required, no fallback)
    // MediaSourceEngine requires buffer data and cannot use URL-based segments
    try {
      const audio = await this.dataProvider.fetchSegmentAudio(segment.id);
      return { segment, audio };
    } catch (error) {
      debug('error fetching audio buffer for segment id=%d: %s', segment.id, error);
      // MediaSourceEngine requires buffer data, cannot fallback to URL
      throw new Error(
        `Failed to fetch audio buffer for segment ${segment.id}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }
}
